import React, { useEffect, useState } from 'react';
import { Confirm, useNotify, useTranslate } from 'react-admin';
import {
    Alert,
    Box,
    CircularProgress,
    FormControlLabel,
    Paper,
    Switch,
    Typography,
} from '@mui/material';
import {
    doc,
    getFirestore,
    onSnapshot,
    serverTimestamp,
    setDoc,
    Timestamp,
} from 'firebase/firestore';
import { format } from 'date-fns';
import { auth, firebaseApp } from '../../providers/firebase';

const db = getFirestore(firebaseApp);

// Read live by the radio web and mobile apps. Kept out of configuration/*,
// which the sync service overwrites.
const featuresDocRef = doc(db, 'appConfig', 'features');

interface AppFeatures {
    gameEnabled?: boolean;
    updatedBy?: string | null;
    updatedAt?: Timestamp | null;
}

function useAppFeatures() {
    const [features, setFeatures] = useState<AppFeatures>({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            featuresDocRef,
            (docSnap) => {
                setFeatures((docSnap.data() as AppFeatures | undefined) ?? {});
                setLoadError(null);
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching app features:', error);
                setLoadError(error.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    return { features, loading, loadError };
}

const LineupGamePage: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const { features, loading, loadError } = useAppFeatures();
    // The value the admin is switching to, while the confirm dialog is open.
    const [pendingValue, setPendingValue] = useState<boolean | null>(null);
    const [saving, setSaving] = useState(false);

    const gameEnabled = features.gameEnabled === true;
    const updatedAt = features.updatedAt instanceof Timestamp ? features.updatedAt.toDate() : null;

    const handleConfirm = async () => {
        if (pendingValue === null) return;
        setSaving(true);
        try {
            await setDoc(
                featuresDocRef,
                {
                    gameEnabled: pendingValue,
                    updatedBy: auth.currentUser?.email ?? null,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            notify(pendingValue ? 'lineupGamePage.enableSuccess' : 'lineupGamePage.disableSuccess', { type: 'success' });
        } catch (error) {
            console.error('Error updating the Lineup game switch:', error);
            notify('lineupGamePage.saveError', { type: 'error' });
        } finally {
            setSaving(false);
            setPendingValue(null);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Typography variant="h4" gutterBottom>
                {translate('lineupGamePage.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {translate('lineupGamePage.subtitle')}
            </Typography>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {translate('lineupGamePage.loadError')}: {loadError}
                </Alert>
            )}

            <Paper elevation={2} sx={{ p: 3 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={gameEnabled}
                            disabled={saving || Boolean(loadError)}
                            onChange={(event) => setPendingValue(event.target.checked)}
                        />
                    }
                    label={translate(gameEnabled ? 'lineupGamePage.enabled' : 'lineupGamePage.disabled')}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {updatedAt
                        ? translate('lineupGamePage.lastChanged', {
                              by: features.updatedBy ?? '-',
                              at: format(updatedAt, 'MMM d, yyyy HH:mm:ss'),
                          })
                        : translate('lineupGamePage.neverChanged')}
                </Typography>
            </Paper>

            <Confirm
                isOpen={pendingValue !== null}
                loading={saving}
                title={pendingValue ? 'lineupGamePage.confirmEnableTitle' : 'lineupGamePage.confirmDisableTitle'}
                content={pendingValue ? 'lineupGamePage.confirmEnableContent' : 'lineupGamePage.confirmDisableContent'}
                onConfirm={handleConfirm}
                onClose={() => setPendingValue(null)}
            />
        </Box>
    );
};

export default LineupGamePage;
